import { BUSINESS_TIME_ZONE } from './dateTime';

export const AUTO_END_BREAK_MINUTES = 60;
export const DEFAULT_MINIMUM_HALF_DAY_MINUTES = 240;

export type CheckoutBreakPrompt =
  | { kind: 'none' }
  | { kind: 'missing' }
  | { kind: 'open'; startedAt: string | null };

export type TodayBreakContext = {
  expectedWorkMinutes?: number | null;
  minimumHalfDayMinutes?: number | null;
  isHalfDay?: boolean | null;
  currentStatus?: string | null;
  breakMinutes?: number | null;
  isOnBreak?: boolean;
  canEndBreak?: boolean;
  openBreakStartedAt?: string | null;
  hasCompletedBreak?: boolean | null;
};

export type AttendanceEventLike = {
  eventType?: string | null;
  localTimestamp?: string | null;
  timestamp?: string | null;
};

export type BreakTimeDraft = {
  start: string | null;
  end: string | null;
  durationMinutes: number | null;
};

const CLOCK = /^(\d{1,2}):(\d{2})$/;
const CLOCK_AMPM = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
/** Naive wall-clock datetime with no Z/offset. ISO instants must not match this. */
const NAIVE_DATE_TIME =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/;

export function isFullDayWork(context: TodayBreakContext): boolean {
  if (context.isHalfDay === true) return false;
  if (context.currentStatus?.trim().toUpperCase() === 'HALF_DAY') return false;
  const expected = context.expectedWorkMinutes;
  const halfDayFloor =
    typeof context.minimumHalfDayMinutes === 'number' &&
    Number.isFinite(context.minimumHalfDayMinutes) &&
    context.minimumHalfDayMinutes > 0
      ? context.minimumHalfDayMinutes
      : DEFAULT_MINIMUM_HALF_DAY_MINUTES;
  if (typeof expected === 'number' && Number.isFinite(expected) && expected > 0) {
    return expected > halfDayFloor;
  }
  return true;
}

function minutesBetweenTimestamps(
  start: string | null,
  end: string | null,
): number | null {
  if (!start || !end) return null;
  const startMinutes = parseClockToMinutes(timestampToClock(start) ?? start);
  const endMinutes = parseClockToMinutes(timestampToClock(end) ?? end);
  if (startMinutes === null || endMinutes === null) return null;
  return endMinutes >= startMinutes
    ? endMinutes - startMinutes
    : endMinutes + 24 * 60 - startMinutes;
}

export type BreakSegment = {
  startAt: string | null;
  endAt: string | null;
  durationMinutes: number | null;
  isOpen: boolean;
};

/** Pair BREAK_OUT → BREAK_IN events into segments. Open breaks have no duration. */
export function breakSegmentsFromEvents(
  events: AttendanceEventLike[],
): BreakSegment[] {
  const ordered = [...events].sort((left, right) => {
    const leftKey = left.localTimestamp ?? left.timestamp ?? '';
    const rightKey = right.localTimestamp ?? right.timestamp ?? '';
    return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
  });

  const segments: BreakSegment[] = [];
  let openStart: string | null = null;
  for (const event of ordered) {
    const type = event.eventType?.trim().toUpperCase();
    const at = event.localTimestamp ?? event.timestamp ?? null;
    if (type === 'BREAK_OUT') {
      if (openStart) {
        segments.push({
          startAt: openStart,
          endAt: null,
          durationMinutes: null,
          isOpen: true,
        });
      }
      openStart = at;
    } else if (type === 'BREAK_IN' && openStart) {
      const minutes = minutesBetweenTimestamps(openStart, at);
      segments.push({
        startAt: openStart,
        endAt: at,
        durationMinutes: minutes !== null && minutes > 0 ? minutes : null,
        isOpen: false,
      });
      openStart = null;
    }
  }
  if (openStart) {
    segments.push({
      startAt: openStart,
      endAt: null,
      durationMinutes: null,
      isOpen: true,
    });
  }
  return segments;
}

export function breakStateFromEvents(events: AttendanceEventLike[]): {
  hasCompletedBreak: boolean;
  openBreakStartedAt: string | null;
  completedBreakMinutes: number | null;
  segments: BreakSegment[];
} {
  const segments = breakSegmentsFromEvents(events);
  let totalMinutes = 0;
  let pairCount = 0;
  let openBreakStartedAt: string | null = null;
  let hasCompletedBreak = false;

  for (const segment of segments) {
    if (segment.isOpen) {
      openBreakStartedAt = segment.startAt;
      continue;
    }
    hasCompletedBreak = true;
    if (segment.durationMinutes != null && segment.durationMinutes > 0) {
      totalMinutes += segment.durationMinutes;
      pairCount += 1;
    }
  }

  return {
    hasCompletedBreak,
    openBreakStartedAt,
    completedBreakMinutes: pairCount > 0 ? totalMinutes : null,
    segments,
  };
}

export function resolveCheckoutBreakPrompt(
  context: TodayBreakContext,
): CheckoutBreakPrompt {
  if (!isFullDayWork(context)) return { kind: 'none' };
  if (
    context.isOnBreak ||
    context.canEndBreak ||
    context.openBreakStartedAt
  ) {
    return { kind: 'open', startedAt: context.openBreakStartedAt ?? null };
  }
  const completed =
    context.hasCompletedBreak === true ||
    (typeof context.breakMinutes === 'number' && context.breakMinutes > 0);
  if (completed) return { kind: 'none' };
  return { kind: 'missing' };
}

export function normalizeClockTime(value: string | null | undefined): string | null {
  const minutes = parseClockToMinutes(value);
  return minutes === null ? null : minutesToClock(minutes);
}

export function minutesToClock(totalMinutes: number): string | null {
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0 || totalMinutes >= 24 * 60) {
    return null;
  }
  const whole = Math.round(totalMinutes);
  const hour = Math.floor(whole / 60);
  const minute = whole % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function parseClockToMinutes(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const ampm = trimmed.match(CLOCK_AMPM);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2]);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
    const isPm = ampm[3].toUpperCase() === 'PM';
    if (hour === 12) hour = isPm ? 12 : 0;
    else if (isPm) hour += 12;
    return hour * 60 + minute;
  }
  const clock = trimmed.match(CLOCK);
  if (!clock) return null;
  const hour = Number(clock[1]);
  const minute = Number(clock[2]);
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return hour * 60 + minute;
}

export function parseDurationToMinutes(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const clock = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (clock) {
    const hours = Number(clock[1]);
    const minutes = Number(clock[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes > 59) {
      return null;
    }
    const total = hours * 60 + minutes;
    return total > 0 ? total : null;
  }
  const labeled = trimmed.match(/^(\d+)\s*h(?:ours?)?(?:\s*(\d+)\s*m(?:in(?:utes?)?)?)?$/i);
  if (labeled) {
    const hours = Number(labeled[1]);
    const minutes = labeled[2] ? Number(labeled[2]) : 0;
    const total = hours * 60 + minutes;
    return total > 0 ? total : null;
  }
  const minutesOnly = trimmed.match(/^(\d+)\s*m(?:in(?:utes?)?)?$/i);
  if (minutesOnly) {
    const total = Number(minutesOnly[1]);
    return total > 0 ? total : null;
  }
  const numeric = Number(trimmed);
  if (Number.isInteger(numeric) && numeric > 0) return numeric;
  return null;
}

export function formatDurationClock(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const remainder = whole % 60;
  return `${hours}:${String(remainder).padStart(2, '0')}`;
}

export function timestampToClock(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const naive = trimmed.match(NAIVE_DATE_TIME);
  if (naive) return naive[2];
  const timeOnly = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (timeOnly) return minutesToClock(parseClockToMinutes(trimmed) ?? -1);
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).format(parsed);
  const match = formatted.match(/(\d{1,2}).(\d{2})/);
  if (!match) return null;
  return minutesToClock(Number(match[1]) * 60 + Number(match[2]));
}

export function currentBusinessClock(now = new Date()): string {
  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).format(now);
  const match = formatted.match(/(\d{1,2}).(\d{2})/);
  if (!match) {
    return minutesToClock(now.getHours() * 60 + now.getMinutes()) ?? '00:00';
  }
  return minutesToClock(Number(match[1]) * 60 + Number(match[2])) ?? '00:00';
}

export function clockOnDateToIso(attendanceDate: string, clock: string): string {
  const minutes = parseClockToMinutes(clock);
  if (minutes === null) {
    throw new Error('Break time is invalid');
  }
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(remainder).padStart(2, '0');
  return new Date(`${attendanceDate}T${hh}:${mm}:00+05:30`).toISOString();
}

export function fillBreakTimes(input: {
  start: string | null;
  end: string | null;
  durationMinutes: number | null;
  changed: 'start' | 'end' | 'duration';
  fallbackEndClock?: string | null;
}): BreakTimeDraft {
  const startMinutes = parseClockToMinutes(input.start);
  const endMinutes = parseClockToMinutes(input.end);
  const fallbackEnd = parseClockToMinutes(input.fallbackEndClock ?? null);
  let duration = input.durationMinutes;

  if (input.changed === 'start' || input.changed === 'end') {
    if (startMinutes !== null && endMinutes !== null && endMinutes > startMinutes) {
      duration = endMinutes - startMinutes;
      return {
        start: minutesToClock(startMinutes),
        end: minutesToClock(endMinutes),
        durationMinutes: duration,
      };
    }
  }

  if (input.changed === 'duration' || duration) {
    if (duration && duration > 0) {
      if (input.changed === 'duration' && startMinutes !== null) {
        const nextEnd = startMinutes + duration;
        return {
          start: minutesToClock(startMinutes),
          end: minutesToClock(nextEnd),
          durationMinutes: duration,
        };
      }
      const knownEnd = endMinutes ?? (input.changed === 'duration' ? fallbackEnd : null);
      if (knownEnd !== null && (input.changed === 'duration' || startMinutes === null)) {
        const nextStart = knownEnd - duration;
        if (nextStart >= 0) {
          return {
            start: minutesToClock(nextStart),
            end: minutesToClock(knownEnd),
            durationMinutes: duration,
          };
        }
      }
      if (startMinutes !== null) {
        const nextEnd = startMinutes + duration;
        return {
          start: minutesToClock(startMinutes),
          end: minutesToClock(nextEnd),
          durationMinutes: duration,
        };
      }
    }
  }

  return {
    start: startMinutes !== null ? minutesToClock(startMinutes) : input.start,
    end: endMinutes !== null ? minutesToClock(endMinutes) : input.end,
    durationMinutes: duration,
  };
}

export function suggestedOpenBreakEndClock(
  startedAt: string | null,
  nowClock: string,
  autoEndMinutes = AUTO_END_BREAK_MINUTES,
): string | null {
  const startMinutes = parseClockToMinutes(timestampToClock(startedAt) ?? startedAt);
  const nowMinutes = parseClockToMinutes(nowClock);
  if (startMinutes === null) return nowClock;
  const autoEnd = startMinutes + autoEndMinutes;
  if (nowMinutes !== null && nowMinutes >= autoEnd) {
    return minutesToClock(autoEnd);
  }
  return nowClock;
}

export function validateCheckoutBreak(input: {
  start: string | null;
  end: string | null;
  durationMinutes: number | null;
  checkInClock?: string | null;
  checkOutClock?: string | null;
  /** An already started break still needs its end time before check-out. */
  requireTimes?: boolean;
}): string | null {
  const startMinutes = parseClockToMinutes(input.start);
  const endMinutes = parseClockToMinutes(input.end);
  if (startMinutes === null && endMinutes === null) {
    if (input.durationMinutes != null && input.durationMinutes > 0) {
      return 'Enter both break start and end, or leave both blank.';
    }
    return input.requireTimes ? 'Enter the break end time.' : null;
  }
  if (startMinutes === null || endMinutes === null) {
    if (input.requireTimes && startMinutes !== null && endMinutes === null) {
      return 'Enter the break end time.';
    }
    return 'Enter both break start and end, or leave both blank.';
  }
  if (endMinutes <= startMinutes) {
    return 'Break end time must be after the start time.';
  }
  const duration = endMinutes - startMinutes;
  if (
    input.durationMinutes != null &&
    input.durationMinutes > 0 &&
    input.durationMinutes !== duration
  ) {
    return 'Break duration must match the start and end times.';
  }
  const checkIn = parseClockToMinutes(input.checkInClock ?? null);
  if (checkIn !== null && startMinutes < checkIn) {
    return 'Break start must be after check-in.';
  }
  const checkOut = parseClockToMinutes(input.checkOutClock ?? null);
  if (checkOut !== null && endMinutes > checkOut) {
    return 'Break end must be at or before check-out.';
  }
  return null;
}
